import { ofType } from 'redux-observable';
import { of, forkJoin } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';

import ajaxWithHealthCheck$ from 'api/ajaxWithHealthCheck';
import { getBookingById$ } from 'api/tretail/booking';
import { retrieveBooking$ } from 'api/tretail/bookingHistory';
import catchInternalServerError from 'store/catchInternalServerError';
import { fetchAppStatusFulfilled } from 'store/appStatus/appStatus.slice';
import { retrieveAllHotelProductPostBookingComments$ } from 'api/tretail/bookingProductCust';
import get from 'lodash/get';
import set from 'lodash/set';
import find from 'lodash/find';
import {
	fetchBooking,
	fetchBookingFailed,
	fetchBookingFulfilled,
	fetchBookingCancel,
	webCheckInErrorEvt,
} from '../webCheckIn.slice';

// tags an error with the lookup call it came from, so failures can be told apart in APM
const tagLookupFailure = failureReason =>
	catchError(error => {
		throw Object.assign(error, {
			lookupFailureReason:
				typeof failureReason === 'function' ? failureReason(error) : failureReason,
		});
	});

// tRetail answers a lookup it can't match with a 4xx or with apiErrors/supplierErrors, even on a 500 (OWS "Supplier
// errors in response" covers both a wrong confirmation number and a booking OWS won't return). A failure without either
// (timeout, gateway error, non-JSON body) means the lookup itself broke, so it mustn't be reported as an expected not_found.
const retrieveFailureReason = ({ status, response }) =>
	(status >= 400 && status < 500) || response?.apiErrors?.length || response?.supplierErrors?.length
		? 'not_found'
		: 'retrieval_error';

export function fetchBooking$({ reservationId, hotelCode, surname, locale }) {
	return retrieveBooking$({
		reservationId,
		hotelCode,
		surname,
		locale,
	}).pipe(
		tagLookupFailure(retrieveFailureReason),
		switchMap(({ bookingId }) => {
			const parts = {
				booking: getBookingById$({
					bookingId,
					locale,
				}).pipe(tagLookupFailure('booking_detail_error')),
				comments: retrieveAllHotelProductPostBookingComments$({
					bookingId,
					locale,
				}).pipe(tagLookupFailure('comments_error')),
			};

			return forkJoin(parts).pipe(
				map(({ booking = {}, comments = {} }) => {
					const childrenCount = get(
						booking,
						['hotelProducts', 0, 'guestCount', 'numberChildren'],
						''
					);

					if (childrenCount === 0) {
						const productId = get(booking, ['hotelProducts', 0, 'productId'], '');
						const comment = find(comments.comments, ['productId', productId]);

						const numberChildren = get(comment, ['roomGuests'], []).reduce(
							(acc, { type }) => (type === 2 ? acc + 1 : acc),
							0
						);
						set(booking, ['hotelProducts', 0, 'guestCount', 'numberChildren'], numberChildren);
					}

					return booking;
				}),

				catchInternalServerError()
			);
		})
	);
}

export default function fetchBookingEpic(action$) {
	return action$.pipe(
		ofType(fetchBooking.type),
		switchMap(({ payload: { reservationId, hotelCode, surname, locale } }) =>
			ajaxWithHealthCheck$({
				locale,
			}).pipe(
				switchMap(() =>
					fetchBooking$({
						reservationId,
						hotelCode,
						surname,
						locale,
					}).pipe(
						// a 500 from the booking/comments calls comes back as fetchAppStatusFulfilled, pass it through as is
						map(result =>
							result?.type === fetchAppStatusFulfilled.type ? result : fetchBookingFulfilled(result)
						),

						catchError(({ response, status, lookupFailureReason }) =>
							of(
								fetchBookingFailed({
									apiErrors: response?.apiErrors || [],
									supplierErrors: response?.supplierErrors || [],
									failureReason: lookupFailureReason || 'unknown',
									errorCode:
										response?.apiErrors?.[0]?.errorCode ||
										response?.supplierErrors?.[0]?.errorCode ||
										(status ? `HTTP ${status}` : ''),
								}),
								webCheckInErrorEvt({
									hotelCode,
									error: 'invalid request',
								})
							)
						),

						takeUntil(action$.pipe(ofType(fetchBookingCancel.type)))
					)
				)
			)
		)
	);
}
